import React, { useState } from 'react';
import classNames from 'classnames';
import { RadioGroup, RadioGroupProps } from '@/components/common/Radio';
import styles from './ToggleTabs.module.scss';

export interface ToggleTabsProps<T extends string> {
  tab: T;
  className?: string;
  tabClassName?: string;
  options: RadioGroupProps<T>['options'];
  buttonColor?: RadioGroupProps['buttonColor'];
  onClick(tab: T): void;
}

export function useToggleTabs<T>(initialType: T) {
  const [viewTab, setViewTab] = useState<T>(initialType);
  return {
    viewTab,
    setViewTab,
  };
}

export const ToggleTabs = <T extends string>({
  className,
  tabClassName,
  buttonColor,
  tab,
  options,
  onClick,
}: ToggleTabsProps<T>) => (
  <RadioGroup
    className={classNames(styles.root, className)}
    buttonClassName={tabClassName}
    buttonColor={buttonColor}
    type="button"
    defaultValue={tab}
    options={options}
    onChange={onClick}
  />
);

export function useToggleTabsComponent<T extends string>({
  initialTab,
  options,
}: Pick<ToggleTabsProps<T>, 'options'> & { initialTab: T }) {
  const { viewTab, setViewTab } = useToggleTabs<T>(initialTab);

  const Component = React.useMemo(
    () => (props?: Partial<Omit<ToggleTabsProps<T>, 'tab' | 'onClick' | 'options'>>) => (
      <ToggleTabs<T> tab={viewTab} onClick={setViewTab} options={options} {...props} />
    ),
    [viewTab, setViewTab, options],
  );

  return { viewTab, ToggleTabs: Component };
}
